import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { addToast, Button, Input, Select, SelectItem } from '@heroui/react'
import { useTheme } from '@/ui/theme'
import { RefundAction } from '@itsrever/returns-api-types'
import { useNavigate, usePages, useTranslate } from '@/hooks'
import { useCreateLiteOrder } from '@/hooks/use-create-lite-order'
import { useItemReturnReasons } from '@/hooks/use-item-return-reasons'
import { addReturnedItems, ItemActions } from '@/redux/return/return-slice'
import { setOrderProducts } from '@/redux/order/order-slice'
import { setOrder } from '@/redux/api/returns-api-slice'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { BoxIsoIcon } from '@/ui/icons/box-iso'
import { PlusIcon } from '@/ui/icons/plus'
import { TrashIcon } from '@/ui/icons/trash'
import { QuantitySelector } from '@/ui/components/quantity-selector'

const schema = z.object({
  products_to_return: z.array(
    z.object({
      product_name: z
        .string()
        .min(1, { message: 'rever_lite.product_name_required' }),
      quantity: z.number(),
      parent_reason: z.string().optional(),
      reason: z
        .string()
        .min(1, { message: 'rever_lite.return_reason_required' })
    })
  )
})

type ItemSelectionLiteData = z.infer<typeof schema>

export const LiteItemSelection = () => {
  const { t } = useTranslation()
  const { translate } = useTranslate()

  const { pages } = usePages()
  const { navigateRelative } = useNavigate()

  const theme = useTheme()

  const dispatch = useAppDispatch()

  const order = useAppSelector((store) => store.order)

  const returnedItems = useAppSelector((store) => store.return.returnedItems)

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const { mutate, status: createLiteOrderStatus } = useCreateLiteOrder()

  const { data, status: returnReasonsStatus } = useItemReturnReasons({
    ecommerce_id: ecommerceId
  })

  const {
    register,
    control,
    trigger,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<ItemSelectionLiteData>({
    defaultValues: {
      products_to_return:
        returnedItems.length > 0
          ? returnedItems.map((item, index) => ({
              product_name: order.orderProducts[index].product.name,
              quantity: item.item.quantity,
              parent_reason: item.item.product_return_reason,
              reason: item.item.product_return_reason
            }))
          : [{ product_name: '', quantity: 1, parent_reason: '', reason: '' }]
    },
    resolver: zodResolver(schema),
    mode: 'onChange'
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products_to_return'
  })

  const watchedProducts = useWatch({ control, name: 'products_to_return' })

  const getSubReasons = (parentName?: string) =>
    data?.return_reasons?.find(
      ({ parent_reason }) => parent_reason?.name === parentName
    )?.available_reasons ?? []

  const getParentReason = (reason?: string) =>
    data?.return_reasons?.find(
      ({ parent_reason, available_reasons }) =>
        parent_reason?.name === reason ||
        available_reasons?.some((sub) => sub.name === reason)
    )?.parent_reason?.name

  const onSubmit = ({ products_to_return }: ItemSelectionLiteData) => {
    if (ecommerceId) {
      mutate(
        {
          ecommerceId,
          returnsCreateLiteOrderRequest: {
            customer_printed_order_id: order.orderNumber,
            ecommerce_id: ecommerceId,
            email: order.email,
            products_to_return: products_to_return.map(
              ({ product_name, quantity, reason }) => ({
                product_name,
                quantity,
                reason
              })
            )
          }
        },
        {
          onSuccess: (response) => {
            const lineItems = response?.order?.line_items ?? []

            const returnedItems = products_to_return.map((product, index) => {
              const lineItemId = lineItems[index]?.id ?? ''
              return {
                item: {
                  line_item_id: lineItemId,
                  quantity: product.quantity,
                  product_return_reason: product.reason,
                  action: RefundAction.RefundAction_TO_RETURN
                },
                lineItemId,
                subProductId: 0,
                action: ItemActions.ToReturn
              }
            })

            dispatch(setOrder(response))
            dispatch(setOrderProducts(response?.order?.line_items))
            dispatch(addReturnedItems(returnedItems))

            navigateRelative(pages.ReturnAddress)
          },
          onError: () => {
            addToast({
              title: t('toast_errors.something_went_wrong'),
              description: t('toast_errors.error_406')
            })
          }
        }
      )
    }
  }

  return (
    <div className="relative flex h-screen flex-1 flex-col overflow-hidden">
      <main className="flex-1 overflow-y-auto bg-grey-6">
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center space-y-8 p-4 md:p-5">
          <h1
            className="text-center font-roobert text-xl font-normal"
            dangerouslySetInnerHTML={{
              __html: translate('item_selection_title')
            }}
          />
          <div className="w-full space-y-5">
            <div className="w-fit rounded-lg bg-neutral-200 px-2.5 py-1">
              <p className="text-xs font-normal text-neutral-950">
                <span
                  className="text-neutral-600"
                  dangerouslySetInnerHTML={{
                    __html: translate('item_selection_order')
                  }}
                />{' '}
                {order.orderNumber ?? '-'}
              </p>
            </div>
            <form className="space-y-10" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4">
                <ul className="flex flex-col gap-3">
                  <AnimatePresence>
                    {fields.map((item, index) => {
                      const stored = watchedProducts?.[index]
                      const selectedParent =
                        getParentReason(stored?.reason) ?? stored?.parent_reason
                      const subReasons = getSubReasons(selectedParent)
                      const hasSubReasons = subReasons.length > 0
                      const reasonError =
                        errors.products_to_return?.[index]?.reason

                      return (
                        <motion.li
                          key={item.id}
                          layout="position"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{
                            opacity: 0,
                            scale: 0.95,
                            transition: { duration: 0.15, ease: 'easeOut' }
                          }}
                          transition={{
                            duration: 0.25,
                            ease: [0.25, 1, 0.5, 1]
                          }}
                          className="relative flex flex-col gap-4 overflow-clip rounded-xl border border-solid border-gray-400/40 bg-white p-5 shadow-sm"
                        >
                          {index !== 0 ? (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              aria-label={t('rever_lite.remove_item')}
                              className="absolute right-1.5 top-1.5 rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 active:bg-neutral-200"
                            >
                              <TrashIcon className="size-4" />
                            </button>
                          ) : null}
                          <div className="flex items-center gap-2 text-neutral-800">
                            <div className="w-fit rounded-md bg-neutral-100 p-2">
                              <BoxIsoIcon className="size-5" />
                            </div>
                            <p className="text-sm">
                              {t('rever_lite.item', { index: index + 1 })}
                            </p>
                          </div>
                          <div className="space-y-4">
                            <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-baseline">
                              <Input
                                {...register(
                                  `products_to_return.${index}.product_name`,
                                  { required: true }
                                )}
                                placeholder={t(
                                  'rever_lite.product_name_placeholder'
                                )}
                                label={t('rever_lite.product_name')}
                                isInvalid={
                                  !!errors.products_to_return?.[index]
                                    ?.product_name
                                }
                                errorMessage={
                                  errors.products_to_return?.[index]
                                    ?.product_name?.message
                                    ? t(
                                        errors.products_to_return?.[index]
                                          ?.product_name?.message
                                      )
                                    : undefined
                                }
                                variant="bordered"
                                radius="sm"
                                classNames={{
                                  base: 'group',
                                  inputWrapper: 'border-solid font-roobert',
                                  input:
                                    'group-data-[invalid]:placeholder:text-danger'
                                }}
                              />
                              <Controller
                                control={control}
                                name={`products_to_return.${index}.quantity`}
                                render={({ field: { onChange, value } }) => (
                                  <QuantitySelector
                                    minValue={1}
                                    value={value}
                                    onValueChange={onChange}
                                  />
                                )}
                              />
                            </div>
                            <Controller
                              control={control}
                              name={`products_to_return.${index}.parent_reason`}
                              render={({ field: { onChange } }) => (
                                <Select
                                  label={t('rever_lite.return_reason')}
                                  placeholder={t(
                                    'rever_lite.return_reason_placeholder'
                                  )}
                                  isInvalid={!!reasonError && !hasSubReasons}
                                  errorMessage={
                                    reasonError?.message && !hasSubReasons
                                      ? t(reasonError.message)
                                      : undefined
                                  }
                                  isVirtualized={false}
                                  selectedKeys={
                                    selectedParent ? [selectedParent] : []
                                  }
                                  variant="bordered"
                                  radius="sm"
                                  isLoading={returnReasonsStatus === 'pending'}
                                  onChange={(e) => {
                                    const parentName = e.target.value
                                    onChange(parentName)

                                    setValue(
                                      `products_to_return.${index}.reason`,
                                      getSubReasons(parentName).length > 0
                                        ? ''
                                        : parentName,
                                      { shouldValidate: true }
                                    )
                                  }}
                                  classNames={{
                                    trigger: 'border-solid font-roobert'
                                  }}
                                >
                                  {(data?.return_reasons ?? []).map(
                                    (reason) => (
                                      <SelectItem
                                        key={reason.parent_reason?.name}
                                      >
                                        {translate(
                                          `select_reason_${reason.parent_reason?.name}`
                                        )}
                                      </SelectItem>
                                    )
                                  )}
                                </Select>
                              )}
                            />
                            {hasSubReasons ? (
                              <Controller
                                control={control}
                                name={`products_to_return.${index}.reason`}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    label={t(
                                      'return_reason.select_subreason_title'
                                    )}
                                    placeholder={t(
                                      'rever_lite.return_reason_placeholder'
                                    )}
                                    isInvalid={!!reasonError}
                                    errorMessage={
                                      reasonError?.message
                                        ? t(reasonError.message)
                                        : undefined
                                    }
                                    isVirtualized={false}
                                    selectedKeys={value ? [value] : []}
                                    variant="bordered"
                                    radius="sm"
                                    onChange={(e) => {
                                      onChange(e.target.value)
                                    }}
                                    classNames={{
                                      trigger: 'border-solid font-roobert'
                                    }}
                                  >
                                    {subReasons.map((subReason) => (
                                      <SelectItem key={subReason.name}>
                                        {translate(
                                          `select_reason_${subReason.name}`
                                        )}
                                      </SelectItem>
                                    ))}
                                  </Select>
                                )}
                              />
                            ) : null}
                          </div>
                        </motion.li>
                      )
                    })}
                  </AnimatePresence>
                </ul>
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-neutral-400 p-2.5 text-sm font-medium text-neutral-500 transition-colors hover:border-neutral-600 hover:bg-neutral-200/30 hover:text-neutral-600 active:bg-neutral-200/50"
                  onClick={() =>
                    trigger('products_to_return').then((isValid) => {
                      if (isValid) {
                        append({
                          product_name: '',
                          quantity: 1,
                          reason: ''
                        })
                      }
                    })
                  }
                >
                  <PlusIcon className="size-5" />
                  {t('rever_lite.add_another_item_button')}
                </button>
              </div>
              <div className="flex w-full justify-center">
                <Button
                  type="submit"
                  variant="solid"
                  radius="sm"
                  size="lg"
                  fullWidth
                  style={{
                    backgroundColor: theme.colors.primary.main,
                    color: theme.colors.primary.contrastText,
                    borderRadius: theme.borderRadius,
                    maxWidth: '20rem'
                  }}
                  isLoading={createLiteOrderStatus === 'pending'}
                >
                  {translate('item_selection_button_continue')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  )
}
